import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollRestorationOptions = {
  debounceTime?: number;
  persist?: false | 'localStorage' | 'sessionStorage';
};

export const useScrollRestoration = <T extends HTMLElement>(
  key: string,
  { debounceTime = 100, persist = 'sessionStorage' }: ScrollRestorationOptions = {},
) => {
  const [element, setElement] = useState<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const ref = useCallback((node: T | null) => {
    if (node) {
      setElement(node);
    }
  }, []);

  useEffect(() => {
    if (!element || !persist) {
      return;
    }

    const storageKey = `scrollRestoration-${key}`;
    const storage = persist === 'localStorage' ? localStorage : sessionStorage;
    const saved = storage.getItem(storageKey);

    if (saved) {
      const { scrollTop, scrollLeft } = JSON.parse(saved) as { scrollTop: number; scrollLeft: number };
      element.scrollTo(scrollLeft, scrollTop);
    }
  }, [element, key, persist]);

  useEffect(() => {
    if (!element) {
      return;
    }

    const handleScroll = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const position = { scrollTop: element.scrollTop, scrollLeft: element.scrollLeft };

        if (persist === 'localStorage') {
          localStorage.setItem(`scrollRestoration-${key}`, JSON.stringify(position));
        } else if (persist === 'sessionStorage') {
          sessionStorage.setItem(`scrollRestoration-${key}`, JSON.stringify(position));
        }
      }, debounceTime);
    };

    element.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timerRef.current);
      element.removeEventListener('scroll', handleScroll);
    };
  }, [element, key, debounceTime, persist]);

  return { ref };
};
